import type { ITransport } from './types';

/**
 * Mock transport for local-only event publishing.
 * Used for development, testing, and the debugger panel.
 *
 * This transport is a no-op for connect/disconnect since events
 * are published directly through the event bus.
 */
class MockTransport implements ITransport {
  private eventHandler: ((type: string, payload: unknown) => void) | null = null;
  private connected = false;

  /**
   * Connect to the mock transport.
   * This is a no-op that resolves immediately.
   */
  public async connect(): Promise<void> {
    this.connected = true;
  }

  /**
   * Disconnect from the mock transport.
   * This is a no-op.
   */
  public disconnect(): void {
    this.connected = false;
  }

  /**
   * Register a handler for incoming events.
   * In MockTransport, this handler is called when simulateEvent is used.
   *
   * @param handler - Callback invoked with event type and payload
   */
  public onEvent(handler: (type: string, payload: unknown) => void): void {
    this.eventHandler = handler;
  }

  /**
   * Whether the transport is currently connected.
   * Always returns true for MockTransport.
   */
  public get isConnected(): boolean {
    return this.connected;
  }

  /**
   * Simulate an incoming event from a remote source.
   * This is used for testing and the debugger panel.
   *
   * @param type - The event type
   * @param payload - The event payload
   */
  public simulateEvent(type: string, payload: unknown): void {
    if (this.eventHandler) {
      this.eventHandler(type, payload);
    }
  }
}

export default MockTransport;
